import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceLayout from "@/components/ServiceLayout";
import PhaseHero from "@/components/how-it-works/PhaseHero";
import StrategySection from "@/components/strategy/StrategySection";
import { Compass } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const serviceDesignImg = "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/services-heroes/Graphics-25.png";

const strategySections = [
  {
    title: "Service Design Thinking",
    description: "Frame every intervention around users, operations, and the service model — subscription, Robot-as-a-Service, or pay-per-use.",
    processes: [
      { id: 1, text: "Map users, processes, and pain points" },
      { id: 2, text: "Define the target service and payment model" },
      { id: 3, text: "Generate alternative technical solutions" },
      { id: 4, text: "Prioritize hypotheses to test" },
    ],
    outcomes: [
      { id: 1, text: "Clear service concept" },
      { id: 2, text: "Shortlist of testable alternatives" },
      { id: 3, text: "Cross-functional alignment" },
    ],
    timeframe: "1 - 3 WEEKS",
  },
  {
    title: "Experimentation Frameworks",
    description: "Design testable experiments that validate transformation hypotheses.",
    processes: [
      { id: 1, text: "Identify high-impact initiatives" },
      { id: 2, text: "Structure testable transformation experiments" },
      { id: 3, text: "Enable iterative validation and learning" },
      { id: 4, text: "Build experiment portfolio" },
    ],
    outcomes: [
      { id: 1, text: "Prioritized experiment portfolio" },
      { id: 2, text: "Validated transformation approach" },
      { id: 3, text: "Iterative learning cycles" },
    ],
    timeframe: "2 - 4 WEEKS",
  },
  {
    title: "Proof of Concept (PoC) & Prototyping",
    description: "Build and test early-stage models or digital solutions in controlled or simulated environments.",
    processes: [
      { id: 1, text: "Simulation of alternative solutions" },
      { id: 2, text: "Digital twin and layout modelling" },
      { id: 3, text: "Prototype pilot cells" },
    ],
    outcomes: [
      { id: 1, text: "Working prototype" },
      { id: 2, text: "Compared technical alternatives" },
    ],
    timeframe: "2 - 6 WEEKS",
  },
  {
    title: "Access to Specialized Labs & Sandboxes",
    description: "Utilize physical, digital, or living labs, such as robotics testing floors, AI testbeds, or secure software sandboxes.",
    processes: [
      { id: 1, text: "Robotics testing floors" },
      { id: 2, text: "AI testbeds" },
      { id: 3, text: "Secure software sandboxes" },
    ],
    outcomes: [
      { id: 1, text: "Real-world test conditions" },
      { id: 2, text: "No disruption to live operations" },
    ],
    timeframe: "ON DEMAND",
  },
  {
    title: "Feasibility Studies & Audits",
    description: "Run technical evaluations, energy audits, cybersecurity vulnerability checks, and digital maturity assessments.",
    processes: [
      { id: 1, text: "Technical evaluation" },
      { id: 2, text: "Energy audit" },
      { id: 3, text: "Cybersecurity vulnerability check" },
      { id: 4, text: "Digital maturity assessment" },
    ],
    outcomes: [
      { id: 1, text: "Feasibility report" },
      { id: 2, text: "Cost-effectiveness verdict" },
    ],
    timeframe: "2 - 4 WEEKS",
  },
  {
    title: "Funding & Expertise",
    description: "Receive guidance from technical experts and explore public or private investment pathways to scale successful tests.",
    processes: [
      { id: 1, text: "Technical expert guidance" },
      { id: 2, text: "Public funding pathways" },
      { id: 3, text: "Private investment pathways" },
    ],
    outcomes: [
      { id: 1, text: "Funding plan to scale" },
      { id: 2, text: "Investment-ready business case" },
    ],
    timeframe: "ONGOING",
  },
];

const DataAnalysis = () => {
  useDocumentTitle(
    "Experimentation — Advisory | Klexu",
    "Feasibility studies on Industry 4.0 technologies: verify the potential and cost-effectiveness of automation, Robot-as-a-Service and pay-per-use before investing."
  );

  return (
    <>
      <Navbar />
      <PhaseHero
        number="02"
        eyebrow="Experimentation"
        titleLead="Experiment before you"
        titleHighlight="invest."
        body="We provide consulting through feasibility studies on Industry 4.0 technologies. Companies looking to automate or digitize their processes with subscriptions, Robot-as-a-Service, or pay-per-use can experimentally verify the potential and cost-effectiveness of planned interventions before investing — evaluating alternative technical solutions through simulation tools and pilot cells prototyped with laboratory technologies."
        whatYouGet="Feasibility studies and audits. Simulated and prototyped alternatives. A funding pathway to scale what works."
        whoItsFor="Companies planning automation or digitization that want proof of value before committing capital."
        ctaLabel="Start an Experiment"
        image={serviceDesignImg}
      />
      <ServiceLayout title="Experimentation" description="" icon={<Compass className="h-8 w-8 text-accent" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {strategySections.map((section, index) => (
            <StrategySection key={index} title={section.title} description={section.description} processes={section.processes} outcomes={section.outcomes} timeframe={section.timeframe} />
          ))}
        </div>
        <Separator className="my-12 bg-white/10" />

        {/* What Makes This Different */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4 text-muted-foreground">Instead of...</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-destructive mt-1">✕</span>
                <span className="text-muted-foreground">Generic strategy reports</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-destructive mt-1">✕</span>
                <span className="text-muted-foreground">Static recommendations</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-destructive mt-1">✕</span>
                <span className="text-muted-foreground">High-level transformation roadmaps</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4 text-foreground">You get...</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-accent mt-1">✓</span>
                <span>Service design thinking</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent mt-1">✓</span>
                <span>Simulations and prototyped pilot cells</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent mt-1">✓</span>
                <span>Feasibility studies with measured results</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent mt-1">✓</span>
                <span>Experiment-driven investment decisions</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-12 bg-white/10" />

        <div className="mt-6">
          <blockquote className="border-l-4 border-accent pl-6 py-4 my-8">
            <p className="text-xl font-semibold italic text-foreground">
              "Prove it in the lab before you pay for it on the floor."
            </p>
          </blockquote>
          
          <p className="text-muted-foreground mb-4">
            Before any investment, we test it. Through service design thinking, experimentation frameworks, simulations, and specialized labs, companies see the real potential and cost-effectiveness of automation and digital solutions — then scale what works with the right expertise and funding.
          </p>
          
          <a
            href="https://studio.luisprato.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Open Contract Studio in a new tab"
          >
            <Compass className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-accent">Powered by Contract Studio™</span>
          </a>
        </div>
      </ServiceLayout>
      <Footer />
    </>
  );
};

export default DataAnalysis;
